/**
 * 프로필 이미지가 없거나 오류가 발생했을 때 기본 이미지를 반환하는 함수
 */
export const getProfileImageWithFallback = (
  profileImage?: string | null
): string => {
  if (!profileImage || profileImage.trim() === "") {
    return `${process.env.PUBLIC_URL}/images/icons/no-image.png`;
  }
  return profileImage;
};

/**
 * 이미지 로드 에러 핸들러 - no-image.png로 대체
 */
export const handleImageError = (
  event: React.SyntheticEvent<HTMLImageElement, Event>
) => {
  const target = event.target as HTMLImageElement;
  target.src = `${process.env.PUBLIC_URL}/images/icons/default-profile.png`;
};

/**
 * Firebase Storage URL에 최적화 파라미터 추가
 */
export const optimizeFirebaseImageUrl = (
  url: string,
  width?: number,
  height?: number,
  quality?: number
): string => {
  if (!url || !url.includes("firebasestorage.googleapis.com")) {
    return url;
  }

  try {
    const urlObj = new URL(url);
    const params = new URLSearchParams();

    // 이미지 크기 최적화
    if (width) {
      params.append("w", width.toString());
    }
    if (height) {
      params.append("h", height.toString());
    }

    // 품질 최적화 (1-100, 기본값 85)
    if (quality && quality >= 1 && quality <= 100) {
      params.append("q", quality.toString());
    } else {
      params.append("q", "85"); // 기본 품질
    }

    // 자동 포맷 최적화 (WebP 지원)
    params.append("f", "auto");

    // 스마트 크롭 (얼굴 인식 크롭)
    params.append("c", "smart");

    // 기존 URL에 파라미터 추가
    const existingParams = urlObj.search;
    const separator = existingParams ? "&" : "?";

    return url + separator + params.toString();
  } catch (error) {
    console.warn("이미지 URL 최적화 실패:", error);
    return url;
  }
};

/**
 * 반응형 이미지 크기별 URL 생성
 */
export const getResponsiveImageUrls = (url: string) => {
  if (!url || !url.includes("firebasestorage.googleapis.com")) {
    return {
      thumbnail: url,
      small: url,
      medium: url,
      large: url,
      original: url,
    };
  }

  return {
    thumbnail: optimizeFirebaseImageUrl(url, 150, 150, 70), // 썸네일
    small: optimizeFirebaseImageUrl(url, 300, 300, 80), // 작은 크기
    medium: optimizeFirebaseImageUrl(url, 600, 600, 85), // 중간 크기
    large: optimizeFirebaseImageUrl(url, 1200, 1200, 90), // 큰 크기
    original: url, // 원본
  };
};

/**
 * 피드 이미지 최적화 (용도별)
 */
export const getFeedImageUrl = (
  url: string,
  usage: "thumbnail" | "feed" | "fullscreen" = "feed"
): string => {
  if (!url || !url.includes("firebasestorage.googleapis.com")) {
    return url;
  }

  switch (usage) {
    case "thumbnail":
      return optimizeFirebaseImageUrl(url, 200, 200, 75);
    case "feed":
      return optimizeFirebaseImageUrl(url, 800, 600, 85);
    case "fullscreen":
      return optimizeFirebaseImageUrl(url, 1920, 1080, 90);
    default:
      return optimizeFirebaseImageUrl(url, 800, 600, 85);
  }
};

/**
 * 프로필 이미지 최적화
 */
export const getProfileImageUrl = (
  url: string,
  size: "small" | "medium" | "large" = "medium"
): string => {
  if (!url || !url.includes("firebasestorage.googleapis.com")) {
    return getProfileImageWithFallback(url);
  }

  const sizes = {
    small: 50,
    medium: 100,
    large: 200,
  };

  const targetSize = sizes[size];
  return optimizeFirebaseImageUrl(url, targetSize, targetSize, 85);
};

/**
 * 이미지 preload 함수
 */
export const preloadImage = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};

/**
 * 여러 이미지 preload 함수
 */
export const preloadImages = async (urls: string[]): Promise<void> => {
  try {
    await Promise.all(urls.map((url) => preloadImage(url)));
  } catch (error) {
    console.warn("일부 이미지 preload 실패:", error);
  }
};
